import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withPolaris } from 'Components/protons/Polaris';
import { Heading } from 'Components/molecules/Heading';
import { Link } from 'Components/molecules/Link';
import { Icon } from 'Components/atoms/Icon';

import './_style.scss';

/**
 * MoreReviews component renders a list of reviews with optional title and custom styles.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Array} props.content - Array of review sections to display
 * @param {Object} props.context - Context object containing cssPrefix
 * @param {Object} props.extraClassNames - Additional class names to apply
 * @param {string} props.icon - Icon to use for the link
 * @param {boolean} props.loadStyles - Flag to load default styles
 * @param {string} props.title - Title of the reviews section
 * @param {number} props.titleSize - Size of the title heading
 * @returns {JSX.Element} The rendered component
 */
const MoreReviews = ({
  content,
  context: { cssPrefix },
  extraClassNames,
  icon,
  loadStyles,
  title,
  titleSize,
}) => (
  <div className={classNames({
    [`${cssPrefix}__more-reviews`]: loadStyles,
    ...extraClassNames,
  })}
  >
    {title && (
      <Heading
        extraClassNames={{
          [`${cssPrefix}__more-reviews--title`]: true,
        }}
        size={titleSize}
      >
        {title}
      </Heading>
    )}
    {content && (
      <div className={`${cssPrefix}__more-reviews--content`}>
        {content.map((section, i) => (
          <div className={`${cssPrefix}__more-reviews--section`} key={i}>
            <Heading
              extraClassNames={{ [`${cssPrefix}__more-reviews--section-title`]: true }}
              size={5}
            >
              {section?.title}
            </Heading>
            <div className={`${cssPrefix}__more-reviews--section-items`}>
              {section?.items.map((item, j) => (
                <div className={`${cssPrefix}__more-reviews--section-item`} key={j}>
                  <Icon icon={icon || `${cssPrefix}__ico-caret_right`} />
                  <Link
                    extraClassNames={{ [`${cssPrefix}__more-reviews--section-item-link`]: true }}
                    href={item.url}
                    onClick={() => {
                      window.dataLayer.push({
                        event: 'more_reviews.button_click',
                        'more_reviews.section': section.title,
                        'more_reviews.url': item.url,
                      });
                      return true;
                    }}
                  >
                    {item.title}
                  </Link>
                </div>

              ))}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

MoreReviews.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          url: PropTypes.string,
        }),
      ),
    }),
  ),
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  extraClassNames: PropTypes.shape({}),
  loadStyles: PropTypes.bool,
  icon: PropTypes.string,
  title: PropTypes.string,
  titleSize: PropTypes.number,

};

MoreReviews.defaultProps = {
  content: [],
  context: {},
  extraClassNames: {},
  icon: null,
  loadStyles: true,
  title: '',
  titleSize: 4,
};

export default withPolaris(MoreReviews);
